import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, SignInButton } from '@clerk/react';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import { fetchCourses } from '../../lib/lmsApi';

// Mirrors the gates enforced in routes/capstone.js. Stated here because a
// prospective student has to know what earning this involves before starting.
const EXAM_QUESTIONS = 50;
const EXAM_PASS_THRESHOLD = 75;

const CSS = `
  .learn-intro {
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    line-height: 1.75;
    color: ${theme.textPrimary};
    max-width: 65ch;
    margin: 0 0 28px;
  }
  .learn-facts {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    margin: 0 0 40px;
    border-top: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-bottom: 1px solid rgba(var(--ap-gold-rgb), 0.25);
  }
  .learn-fact {
    flex: 1 1 140px;
    padding: 16px 20px 16px 0;
  }
  .learn-fact dt {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    margin: 0 0 6px;
  }
  .learn-fact dd {
    font-family: 'Cormorant Garamond', serif;
    font-size: 26px;
    color: ${theme.gold};
    margin: 0;
    font-variant-numeric: tabular-nums;
  }

  .learn-steps { margin: 0; padding: 0; list-style: none; }
  .learn-step {
    display: flex;
    gap: 22px;
    align-items: flex-start;
  }
  .learn-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    flex-shrink: 0;
  }
  .learn-num {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid rgba(var(--ap-gold-rgb), 0.5);
    color: ${theme.gold};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
    flex-shrink: 0;
  }
  .learn-num.final {
    background: rgba(var(--ap-gold-rgb), 0.14);
    border-color: ${theme.gold};
  }
  .learn-line {
    flex: 1;
    width: 1px;
    background: rgba(var(--ap-gold-rgb), 0.28);
    margin: 6px 0;
    min-height: 20px;
  }
  .learn-card {
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-radius: 4px;
    padding: 26px 30px;
    text-decoration: none;
    display: block;
    flex: 1;
    margin-bottom: 20px;
    transition: border-color 0.2s ease;
  }
  a.learn-card:hover { border-color: rgba(var(--ap-gold-rgb), 0.55); }
  .learn-eyebrow {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    margin: 0 0 8px;
  }
  .learn-card-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 23px;
    color: ${theme.textPrimary};
    margin: 0 0 10px;
    text-wrap: balance;
  }
  .learn-card-desc {
    font-family: 'EB Garamond', serif;
    font-size: 16px;
    color: ${theme.textMuted};
    line-height: 1.65;
    margin: 0;
  }
  .learn-gates {
    margin: 14px 0 0;
    padding: 0;
    list-style: none;
    color: ${theme.textMuted};
    font-size: 15px;
    line-height: 1.7;
  }
  .learn-gates li::before {
    content: '\\2014';
    color: ${theme.gold};
    margin-right: 8px;
  }
  .learn-note {
    color: ${theme.textMuted};
    font-size: 15px;
    line-height: 1.7;
    max-width: 65ch;
    margin: 36px 0 0;
    padding-top: 22px;
    border-top: 1px solid rgba(var(--ap-text-muted-rgb), 0.2);
  }
  .learn-cta { display: flex; flex-wrap: wrap; gap: 18px; align-items: center; margin: 30px 0 4px; }
  .learn-quiet {
    color: ${theme.gold};
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
    letter-spacing: 0.05em;
    text-decoration: none;
  }
  @media (max-width: 600px) {
    .learn-step { gap: 14px; }
    .learn-card { padding: 20px; }
  }
`;

// Course titles are inconsistent in the database: Level 1 is "Austrian
// Economics 101" while Levels 2 and 3 are prefixed "Level N: ...". The card's
// eyebrow already states the level, so strip a redundant prefix rather than
// printing "Level 2 · Level 2: Capital, Money...". Fixing the seeded titles
// themselves would be the tidier answer, but it is content, not presentation.
function displayTitle(title) {
  return title.replace(/^Level\s+\d+\s*[:—-]\s*/i, '');
}

// The levels are a genuine sequence -- Level 2 assumes Level 1's value theory,
// Level 3 assumes Level 2's capital theory -- so numbering them encodes
// something true rather than just decorating the list.
function Step({ n, isLast, children }) {
  return (
    <li className="learn-step">
      <div className="learn-rail">
        <div className={`learn-num${isLast ? ' final' : ''}`}>{isLast ? '★' : n}</div>
        {!isLast && <div className="learn-line" />}
      </div>
      {children}
    </li>
  );
}

function LearnPage() {
  const { isSignedIn } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Diploma in Austrian Economics - Austrian Process';
    fetchCourses()
      .then(setCourses)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const totalLessons = courses.reduce((n, c) => n + (c.lessonCount || 0), 0);

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <PageHeader
        title="Diploma in Austrian Economics"
        description="A self-paced program through the Austrian School, from the theory of value to the business cycle -- ending in an examined credential rather than a certificate of attendance."
      />

      <p className="learn-intro">
        Three levels, taken in order, each building on the last. You finish by doing the two
        things the tradition actually asks of you: defending an argument in writing, and
        demonstrating you can hold the whole framework at once.
      </p>

      <dl className="learn-facts">
        <div className="learn-fact">
          <dt>Levels</dt>
          <dd>{courses.length || ' - '}</dd>
        </div>
        <div className="learn-fact">
          <dt>Lessons</dt>
          <dd>{totalLessons || ' - '}</dd>
        </div>
        <div className="learn-fact">
          <dt>Exam</dt>
          <dd>{EXAM_QUESTIONS} Q</dd>
        </div>
        <div className="learn-fact">
          <dt>To pass</dt>
          <dd>{EXAM_PASS_THRESHOLD}%</dd>
        </div>
        <div className="learn-fact">
          <dt>Cost</dt>
          <dd>Free</dd>
        </div>
      </dl>

      {loading && <p style={{ color: theme.textMuted }}>Loading the program...</p>}
      {error && <p style={{ color: theme.error }}>Couldn't load the program. Please try again shortly.</p>}
      {!loading && !error && courses.length === 0 && (
        <p style={{ color: theme.textMuted }}>No levels published yet.</p>
      )}

      {courses.length > 0 && (
        <ol className="learn-steps">
          {courses.map((course, i) => (
            <Step key={course.slug} n={i + 1}>
              <Link to={`/${course.slug}`} className="learn-card">
                <p className="learn-eyebrow">
                  Level {i + 1}
                  {course.lessonCount ? ` · ${course.lessonCount} lessons` : ''}
                </p>
                <h2 className="learn-card-title">{displayTitle(course.title)}</h2>
                <p className="learn-card-desc">{course.description}</p>
              </Link>
            </Step>
          ))}

          <Step isLast>
            <div className="learn-card">
              <p className="learn-eyebrow">The Capstone · both required</p>
              <h2 className="learn-card-title">How the Diploma is earned</h2>
              <ul className="learn-gates">
                <li>
                  An applied research essay of 2,000&ndash;3,000 words citing at least three
                  primary sources, read and graded by a human against a published rubric.
                </li>
                <li>
                  A {EXAM_QUESTIONS}-question comprehensive exam drawn across all three levels,
                  passed at {EXAM_PASS_THRESHOLD}%.
                </li>
              </ul>
            </div>
          </Step>
        </ol>
      )}

      <div className="learn-cta">
        {isSignedIn ? (
          <>
            <Link to="/my-learning" className="learn-quiet">My Learning &rarr;</Link>
            <Link to="/capstone" className="learn-quiet">Capstone &rarr;</Link>
          </>
        ) : (
          <SignInButton mode="modal">
            <button type="button" className="btn-primary">Create an account to start</button>
          </SignInButton>
        )}
      </div>

      {/* Deliberately honest about what the lessons are: they're short, and the
          reading is where the hours actually go. Overstating the contact time
          would be the easiest thing here and the least defensible. */}
      <p className="learn-note">
        The lessons are deliberately compact - each pairs one idea with the interactive
        tool or primary text that makes it concrete, and most take five to ten minutes to read.
        They are a spine, not a substitute for the sources. The real work of this program is in
        the reading they point you to, and in the essay you will eventually have to defend.
      </p>
    </div>
  );
}

export default LearnPage;
